import { useEffect, useReducer } from 'react';
import type { RefObject } from 'react';

interface AnimationState {
  stage: number;
  isActive: boolean;
}

type AnimationAction = { type: 'ACTIVATE' } | { type: 'PAUSE' } | { type: 'ADVANCE'; to: number };

const initialState: AnimationState = { stage: 0, isActive: false };

const reducer = (state: AnimationState, action: AnimationAction): AnimationState => {
  switch (action.type) {
    case 'ACTIVATE':
      if (state.isActive) return state;
      return { stage: 1, isActive: true };
    case 'PAUSE':
      if (!state.isActive) return state;
      return { stage: 0, isActive: false };
    case 'ADVANCE':
      return { ...state, stage: action.to };
    default:
      return state;
  }
};

interface UseStagePlayOptions {
  /** Highest stage number (inclusive). Animation cycles 1 → final → 1. */
  finalStage: number;
  /** Duration each stage stays visible before advancing. Default 1800ms. */
  stepDuration?: number;
  /** Extra hold time AFTER `finalStage` before looping back to 1. Seconds. */
  pauseAfterStage?: number;
  /** IntersectionObserver threshold for activation. Default 0.25. */
  visibility?: number;
  /** When true, animation loops indefinitely; otherwise plays once. */
  loop?: boolean;
}

interface UseStagePlayResult {
  stage: number;
  isActive: boolean;
}

/**
 * Drives a stage-by-stage animation while the host element is in the viewport.
 *
 * - IntersectionObserver activates when the host enters the viewport.
 * - A single setTimeout chain advances 1 → finalStage. With `loop: true`,
 *   it then wraps back to 1 and continues; with `loop: false`, it stops at
 *   finalStage.
 * - When the host leaves the viewport (or unmounts), it pauses and resets
 *   to stage 0 so re-entry restarts cleanly.
 * - Visual transitions are driven entirely by CSS selectors keyed off the
 *   `data-stage` attribute on the root element.
 */
export const useStagePlay = <T extends HTMLElement | null>(
  ref: RefObject<T>,
  options: UseStagePlayOptions,
): UseStagePlayResult => {
  const {
    finalStage,
    stepDuration = 1800,
    pauseAfterStage = 0,
    visibility = 0.25,
    loop = true,
  } = options;
  const [state, dispatch] = useReducer(reducer, initialState);

  // Mount: observe viewport entry/exit.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          dispatch({ type: 'ACTIVATE' });
        } else {
          dispatch({ type: 'PAUSE' });
        }
      },
      { threshold: visibility },
    );
    observer.observe(root);

    return () => observer.disconnect();
  }, [ref, visibility]);

  // Stage advance: schedule next stage whenever the animation is active.
  useEffect(() => {
    if (!state.isActive) return;
    const reachedEnd = state.stage >= finalStage;
    const delay = reachedEnd ? pauseAfterStage * 1000 : stepDuration;
    const timer = window.setTimeout(() => {
      const next = reachedEnd ? 1 : state.stage + 1;
      dispatch({ type: 'ADVANCE', to: next });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [state.stage, state.isActive, finalStage, stepDuration, pauseAfterStage, loop]);

  return { stage: state.stage, isActive: state.isActive };
};
